import CurrentConditionsLoader from './CurrentConditionsLoader';
import Date from 'components/common/dates/Date';
import Integer from 'components/common/text/Integer';
import { CurrentConditionsDetail } from 'hooks/useGetForecast';
import { useSettingsContext } from 'providers/SettingsProvider';
import { DateFormats, Units, UnitsOfMeasurement } from 'utils/constants';

type CurrentConditionsProps = {
  className?: string;
  isLoading: boolean;
  current?: CurrentConditionsDetail;
  updatedAt?: string;
};

function CurrentConditions({ className, isLoading, current, updatedAt }: CurrentConditionsProps) {
  const settings = useSettingsContext();
  const isMetric = UnitsOfMeasurement.Metric === settings.units;

  if (isLoading) {
    return <CurrentConditionsLoader />;
  }

  if (current) {
    return (
      <div data-testid="current-conditions" className={className}>
        <div className="mb-2">
          <div className="flex items-center">
            <div className="text-2xl">Now</div>
          </div>
          {updatedAt && (
            <div className="text-sm">
              As of <Date date={updatedAt} format={DateFormats.TIME} />
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <img src={`https:${current.condition.icon}`} alt={current.condition.text} />

          <div className="ml-4 text-4xl leading-none">
            <Integer
              value={isMetric ? current.temp_c : current.temp_f}
              unit={isMetric ? Units.Celsius : Units.Fahrenheit}
            />
          </div>
        </div>

        <div className="mb-4 flex justify-between text-xl font-light">
          <div className="mr-4">{current.condition.text}</div>

          <div className="text-right">
            Feels{' '}
            <Integer
              value={isMetric ? current.feelslike_c : current.feelslike_f}
              unit={isMetric ? Units.Celsius : Units.Fahrenheit}
            />
          </div>
        </div>

        <div className="grid grid-flow-col grid-rows-6 gap-x-4 font-light sm:grid-rows-3">
          {/* Humidity */}
          <div className="flex justify-between border-b border-b-slate-300/70 py-2">
            <div>Humidity</div>
            <div className="ml-1">
              <Integer value={current.humidity} unit={Units.Percent} />
            </div>
          </div>
          {/* Cloud Cover */}
          <div className="flex justify-between border-b border-b-slate-300/70 py-2">
            <div>Cloud cover</div>
            <div className="ml-1">
              <Integer value={current.cloud} unit={Units.Percent} />
            </div>
          </div>
          {/* Visibility */}
          <div className="flex justify-between border-b border-b-slate-300/70 py-2">
            <div>Visibility</div>
            <div className="ml-1">
              <Integer
                value={isMetric ? current.vis_km : current.vis_miles}
                unit={isMetric ? Units.Kilometer : Units.Mile}
              />
            </div>
          </div>
          {/* Wind Speed */}
          <div className="flex justify-between border-b border-b-slate-300/70 py-2">
            <div>Wind</div>
            <div className="ml-1">
              <Integer
                value={isMetric ? current.wind_kph : current.wind_mph}
                unit={isMetric ? Units.KilometerPerHour : Units.MilePerHour}
              />{' '}
              {current.wind_dir}
            </div>
          </div>
          {/* Wind Gust Speed */}
          <div className="flex justify-between border-b border-b-slate-300/70 py-2">
            <div>Wind gusts</div>
            <div className="ml-1">
              <Integer
                value={isMetric ? current.gust_kph : current.gust_mph}
                unit={isMetric ? Units.KilometerPerHour : Units.MilePerHour}
              />
            </div>
          </div>
          {/* UV Index */}
          <div className="flex justify-between border-b border-b-slate-300/70 py-2">
            <div>UV index</div>
            <div className="ml-1">{current.uv}</div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default CurrentConditions;
